import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import DashboardCard from './DashboardCard';
import { KpiGrid, KpiTile, KpiLabel, KpiValue, KpiSub } from '../styles';
import { formatPercent, formatNumber } from '../constants';
import { OPEN_REQUISITION_STATUSES } from '../../ats-requisitions/requisitionActions';
import { formatMoney } from '../../ats-requisitions/recruitingCost';

// SPEC F3 — maliyet KPI'larının derin bağlantısı: kadro bazlı işe alım raporu
// (maliyet + işe alım başına maliyet kolonları orada). Pano dönemi rapora
// taşınmaz — rapor kendi (onay tarihi) süzgecini kullanır.
export const RECRUITING_REPORT_PATH = '/ats/reports';

// Kart 1/7 — KPI şeridi: açık kadro, aktif başvuru, dönemde işe alınan, teklif
// kabul oranı, işe alım başına maliyet, dönem işe alım harcaması (SPEC F3). offerAcceptanceRate payda sıfırken backend `null` döner —
// formatPercent bunu "—" gösterir, "%0" ASLA.
//
// Her sayı TIKLANABİLİR ve sayının hesaplandığı KÜMENİN listesine gider —
// desen AgingPoolCard'ın satır tıklamasından alındı (useHistory + push).
// Hedefler backend'in yüklemleriyle BİREBİR aynı kümeyi süzer
// (AtsDashboardDataAccess.ComputeKpiAsync):
//   openRequisitions   → AtsRequisition.Status ∈ {approved, posting_opened}
//   activeApplications → AtsApplication.Status ∈ {active, on_hold, offered}
//   hiredInPeriod      → AtsApplication.Status == accepted
// Kart bir sayı deyip liste başka bir küme gösterirse rakam güvenilmez olur;
// bu yüzden kadro kümesi requisitionActions.js'teki TEK sabitten okunur.
const OPEN_APPLICATION_STATUSES = ['active', 'on_hold', 'offered'];

export default function KpiStripCard({ data, loading }) {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const go = useCallback((target) => {
    if (!target) return;
    history.push(target);
  }, [history]);

  // `target: null` → o kutu tıklanabilir DEĞİLDİR (imleç/hover/rol verilmez).
  // Teklif kabul oranının doğal listesi tekliflerdir; dönem süzgeci teklif
  // listesinde YOK, bu yüzden düz /ats/offers'a gider.
  const tiles = useMemo(() => [
    {
      key: 'openRequisitions',
      accent: '#0291EF',
      label: t('atsDashboard.kpi.openRequisitions'),
      value: formatNumber(data?.openRequisitions, i18n.language),
      sub: t('atsDashboard.kpi.openRequisitionsSub'),
      target: `/ats/requisitions?status=${OPEN_REQUISITION_STATUSES.join(',')}`,
    },
    {
      key: 'activeApplications',
      accent: '#02D3BF',
      label: t('atsDashboard.kpi.activeApplications'),
      value: formatNumber(data?.activeApplications, i18n.language),
      sub: t('atsDashboard.kpi.activeApplicationsSub'),
      target: `/ats/applications?status=${OPEN_APPLICATION_STATUSES.join(',')}`,
    },
    {
      key: 'hiredInPeriod',
      accent: '#01A899',
      label: t('atsDashboard.kpi.hiredInPeriod'),
      value: formatNumber(data?.hiredInPeriod, i18n.language),
      sub: t('atsDashboard.kpi.hiredInPeriodSub'),
      target: '/ats/applications?status=accepted',
    },
    {
      key: 'offerAcceptanceRate',
      accent: '#F59E0B',
      label: t('atsDashboard.kpi.offerAcceptanceRate'),
      value: formatPercent(data?.offerAcceptanceRate, i18n.language),
      sub: t('atsDashboard.kpi.offerAcceptanceRateSub'),
      target: '/ats/offers',
    },
    {
      // Dönemde işe alımı olan kadroların dönem maliyeti / dönemde işe alınan.
      // Payda 0 → backend null → "—" (0 DEĞİL).
      key: 'costPerHire',
      accent: '#7C3AED',
      label: t('atsDashboard.kpi.costPerHire'),
      value: formatMoney(data?.costPerHire, data?.costCurrency, i18n.language),
      sub: t('atsDashboard.kpi.costPerHireSub'),
      target: RECRUITING_REPORT_PATH,
    },
    {
      key: 'recruitingSpend',
      accent: '#EC4899',
      label: t('atsDashboard.kpi.recruitingSpend'),
      value: formatMoney(data?.recruitingSpend, data?.costCurrency, i18n.language),
      sub: t('atsDashboard.kpi.recruitingSpendSub'),
      target: RECRUITING_REPORT_PATH,
    },
  ], [t, i18n.language, data]);

  return (
    <DashboardCard
      cardKey="kpi-seridi"
      title={t('atsDashboard.kpi.title')}
      span={12}
      spanMd={6}
      loading={loading}
      empty={!data}
      domId="ats-dash-kpi"
    >
      <KpiGrid>
        {tiles.map((tile) => (
          <KpiTile
            key={tile.key}
            $accent={tile.accent}
            $clickable={!!tile.target}
            data-role={`ats-kpi-${tile.key}`}
            {...(tile.target ? {
              role: 'button',
              tabIndex: 0,
              // Ekran okuyucuya "şu sayının listesini aç" der; salt etiket
              // ("Açık Kadrolar") bunun bir bağlantı olduğunu söylemezdi.
              'aria-label': t('atsDashboard.kpi.drillDown', { metric: tile.label }),
              onClick: () => go(tile.target),
              onKeyDown: (e) => {
                // Space sayfayı kaydırmasın: rol button ise varsayılan engellenir.
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                  e.preventDefault();
                  go(tile.target);
                }
              },
            } : {})}
          >
            <KpiLabel>{tile.label}</KpiLabel>
            <KpiValue>{tile.value}</KpiValue>
            <KpiSub>{tile.sub}</KpiSub>
          </KpiTile>
        ))}
      </KpiGrid>
    </DashboardCard>
  );
}
